import { PrismicRichText, PrismicText } from '@prismicio/react';
import { hasRichText } from '@publicss/utils';
import classNames from 'classnames';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { gsap } from 'gsap';
import React, { useEffect } from 'react';
import Flickity from 'react-flickity-component';
import styled from 'styled-components';

import cursorActive from '../../assets/images/cursor--active.svg';
import cursorDrag from '../../assets/images/cursor--drag.svg';
import { usePageContext } from '../../providers/PageContext';
import { media } from '../../styles/media';
import { useIsMobile } from '../../utils/breakpoints';
import { Button } from '../ui';

const Section = styled.section`
  .carousel {
    position: relative;
    outline: 0;
    height: 300px;
    cursor: url(${cursorDrag}), auto;
    transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
    @media ${media.mdUp} {
      height: 430px;
    }
    &:active {
      cursor: url(${cursorActive}), auto;
    }
  }
  .flickity-slider {
    position: relative;
  }
  .flickity-viewport {
    overflow: hidden;
    outline: 0;
  }
  .carousel--cell {
    width: 250px;
    padding-inline-end: 20px;
    height: 301px;
    img {
      height: 100%;
      object-fit: cover;
    }
    @media ${media.mdUp} {
      width: 350px;
      height: 432px;
      img {
        height: 100%;
        object-fit: cover;
      }
    }
  }
  .carousel--text {
    position: absolute;
    bottom: 0;
    inset-inline-end: 20px;
    width: 175px;
    text-align: center;
    display: block;
    padding: 12px 0;
    z-index: 10;
    background: var(--grey-2);
    color: var(--grey-5);
  }
`;

const BlockCarouselSlice = ({ slice, sliceIndex }) => {
  const isMobile = useIsMobile();
  const { siteTag } = usePageContext();

  useEffect(() => {
    const fadeUp = document.querySelectorAll('#block-carousel .fade-up');

    fadeUp.forEach(function (element) {
      gsap.from(element, {
        scrollTrigger: {
          trigger: element,
          start: 'top 90%',
        },
        duration: 0.5,
        opacity: '0',
        y: '20%',
      });
    });

    setTimeout(function () {
      window.dispatchEvent(new Event('resize'));
    }, 500);
  }, []);

  const flickityOptions = {
    pageDots: false,
    prevNextButtons: false,
    accessibility: true,
    draggable: true,
    wrapAround: true,
    cellAlign: 'left',
  };

  return (
    <Section id="block-carousel" className="bg--grey-2">
      <div className="container relative z-10 pt-spacing-07 md:pt-24 lg:pt-40">
        <div className="grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12">
          <div className="col-span-4 md:col-span-4 lg:col-span-4">
            <h2
              className={classNames(
                'color--primary-dark fade-up mb-10',
                siteTag === 'site:kitchenvalley' ? 'h2-kv' : 'h2'
              )}>
              <PrismicRichText field={slice.primary.heading?.richText} />
            </h2>
          </div>

          <div className="col-span-4 md:col-span-4 md:col-start-1 lg:col-span-5 lg:col-start-7">
            <p>
              <PrismicText field={slice.primary.description?.richText} />
            </p>
            <div className="fade-up mt-10 flex items-center lg:mt-12">
              {hasRichText(slice.primary.button?.richText) && (
                <Button
                  id={`cta-block-carousel-slice-${sliceIndex}`}
                  globalTypeform
                  fullWidth={isMobile}>
                  <PrismicText field={slice.primary.button?.richText} />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="container">
        <Flickity
          className="carousel mt-20"
          options={flickityOptions}
          reloadOnUpdate>
          {slice.items.map((item, idx) => {
            const { carousel_text, image, alt } = item;
            return (
              <div key={idx} className="carousel--cell">
                <GatsbyImage
                  className="h-full"
                  image={getImage(image)}
                  alt={alt}
                />
                <div className="carousel--text">
                  <span className="eyebrow">
                    <PrismicText field={carousel_text?.richText} />
                  </span>
                </div>
              </div>
            );
          })}
        </Flickity>
      </div>
      <div className="container">
        <div className="grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12">
          <div className="col-span-4 md:pr-16 lg:col-span-3">
            <h3 className="h5 color--grey-6 fade-up mb-6 mt-12 lg:mt-20">
              <PrismicText field={slice.primary.section_heading_1?.richText} />
            </h3>
            <p className="small color--grey-5 fade-up mb-0">
              <PrismicText
                field={slice.primary.section_description_1?.richText}
              />
            </p>
          </div>

          <div className="col-span-4 md:pr-16 lg:col-span-3">
            <h3 className="h5 color--grey-6 fade-up mb-6 mt-12 lg:mt-20">
              <PrismicText field={slice.primary.section_heading_2?.richText} />
            </h3>
            <p className="small color--grey-5 fade-up mb-0">
              <PrismicText
                field={slice.primary.section_description_2?.richText}
              />
            </p>
          </div>

          <div className="col-span-4 md:pr-16 lg:col-span-3">
            <h3 className="h5 color--grey-6 fade-up mb-6 mt-12 lg:mt-20">
              <PrismicText field={slice.primary.section_heading_3?.richText} />
            </h3>
            <p className="small color--grey-5 fade-up mb-0">
              <PrismicText
                field={slice.primary.section_description_3?.richText}
              />
            </p>
          </div>

          <div className="col-span-4 md:pr-16 lg:col-span-3">
            <h3 className="h5 color--grey-6 fade-up mb-6 mt-12 lg:mt-20">
              <PrismicText field={slice.primary.section_heading_4?.richText} />
            </h3>
            <p className="small color--grey-5 fade-up mb-0">
              <PrismicText
                field={slice.primary.section_description_4?.richText}
              />
            </p>
          </div>

          <div className="col-span-4 pt-spacing-07 md:col-span-8 md:pt-24 lg:col-span-12 lg:pt-40">
            <hr />
          </div>
        </div>
      </div>
    </Section>
  );
};

export default BlockCarouselSlice;
